import type { PlayerWithPlugins } from 'video.js';

import type { PossibleResolution, ResolutionOption } from 'bundles/video-player/types/VideoPlayer';
import userPreferences from 'bundles/video-player/utils/userPreferences';
import { getDefaultVideoResolution } from 'bundles/video-player/utils/videoUtils';

import _t from 'i18n!nls/video-player';

export const POSSIBLE_RESOLUTION_OPTIONS = ['234p', '240p', '270p', '360p', '540p', '720p', '1080p', 'auto'];

export const getResolutionOptions = ({
  supportedResolutions,
  enableHls,
  currentResolutionOption,
  currentResolution,
}: {
  supportedResolutions: string[];
  enableHls?: boolean;
  currentResolutionOption: string;
  currentResolution: string;
}) => {
  let supportedResolutionOptions = supportedResolutions;
  if (enableHls) {
    // Temporarily remove auto from supported resolution options so that we can sort the actual resolutions appropriately
    supportedResolutionOptions = supportedResolutions.filter((resolution) => resolution !== 'auto');
  }

  // Sort in descending order so the highest resolution is shown first
  let sortedResolutionOptions = supportedResolutionOptions.sort((a, b) => {
    const resA = parseInt(a, 10);
    const resB = parseInt(b, 10);
    return resB - resA;
  });

  if (enableHls) {
    // Add the auto option back in
    sortedResolutionOptions = ['auto', ...sortedResolutionOptions];
  }

  const resolutionOptions = sortedResolutionOptions.map((resolutionOption) => {
    const isSelected = resolutionOption === currentResolutionOption;

    // We don't want to translate resolutions since they are universally recognized; however, I do think it makes sense to translate the string "Auto"
    // eslint-disable-next-line no-restricted-syntax
    const autoLabel = isSelected && !!currentResolution ? _t('Auto') + ` (${currentResolution})` : _t('Auto');

    return {
      id: resolutionOption,
      // We don't want to translate each resolution since they are universally recognized
      label: resolutionOption === 'auto' ? autoLabel : resolutionOption,
      role: 'menuitemradio',
      isSelected,
    };
  });

  return resolutionOptions;
};

/**
 * Automatically selects the resolution by enabling all quality levels that have a resolution height.
 * This ensures that auto-bandwidth switching works correctly.
 *
 * @param qualityLevels - The list of available quality levels.
 * @param onAutoResolutionEnabled - A callback function to be called after enabling all the resolutions.
 * @returns void
 */
export const enableAutoResolutionSwitching = ({
  player,
  onAutoResolutionEnabled,
}: {
  player: PlayerWithPlugins;
  onAutoResolutionEnabled: () => void;
}) => {
  const qualityLevels = player.qualityLevels();
  // We need to enable all quality levels to ensure auto-bandwidth switching has a list of options to choose from. By doing this, we allow
  // video.js to automatically switch between resolutions based on the user's bandwidth.
  for (let i = 0; i < qualityLevels.length; i += 1) {
    // There could be audio-only renditions in the list of quality levels, so we need to ensure a resolution height exists
    if (qualityLevels[i].height) {
      qualityLevels[i].enabled = true;
    }
  }

  onAutoResolutionEnabled();
};

export const getInitialResolution = ({
  enableHlsExperiment,
  isAutoAllowed = true,
}: {
  enableHlsExperiment: boolean;
  isAutoAllowed: boolean;
}) => {
  const defaultResolution = getDefaultVideoResolution();

  const savedMp4Resolution = userPreferences.get('resolution');
  let resolution: string = POSSIBLE_RESOLUTION_OPTIONS.includes(savedMp4Resolution)
    ? savedMp4Resolution
    : defaultResolution;

  if (enableHlsExperiment) {
    const savedHlsResolution = userPreferences.get('hlsResolution');
    resolution = POSSIBLE_RESOLUTION_OPTIONS.includes(savedHlsResolution) ? savedHlsResolution : 'auto';
  }

  if (!isAutoAllowed && resolution === 'auto') {
    resolution = defaultResolution;
  }

  return resolution;
};

export const trackVisualHlsResolutionChange = ({
  player,
  setCurrentResolution,
}: {
  player: PlayerWithPlugins;
  setCurrentResolution: (resolution: PossibleResolution | '') => void;
}) => {
  player.on('resize', () => {
    const videoHeight = player.videoHeight();
    const visuallyRenderedResolution = `${videoHeight}p` as PossibleResolution;
    setCurrentResolution(visuallyRenderedResolution);
  });
};

export const saveResolutionInUserPreferences = (resolution: string) => {
  userPreferences.set('resolution', resolution);
  userPreferences.set('hlsResolution', resolution);
};
